import { Action } from 'app/services/oauth/constants';
import { loadContinuation, saveContinuation } from 'app/services/oauth/Continuation';
import { InvalidResultError } from 'app/services/oauth/errors';
import OauthError from 'app/services/oauth/OauthError';
import { attachExternalOauth } from 'app/utils/api';
import createErrorType from 'app/utils/createErrorType';
import { decodeOrThrow } from 'app/utils/ioTs';
import { requestToURL } from 'app/utils/request';
import * as t from 'io-ts';
import { FACEBOOK, AuthProvider } from 'app/constants/enums/AuthProvider';
import { EntityStringId } from 'app/types/entity';
import { createGuid } from 'app/types/guid';

export class AttachError extends createErrorType('AttachError') {
  public constructor(
    message: string,
    public readonly provider: AuthProvider,
    public readonly error?: string,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export class UidTakenError extends AttachError {
}

export const startAttachExternalOauth = (
  provider: AuthProvider,
  returnUriReference: string,
  brandId: EntityStringId,
  currentToken: string,
  dmmEnv?: string,
) => {
  const state = createGuid();

  saveContinuation({
    action: Action.ATTACH,
    redirectUriReference: returnUriReference,
    provider,
    state,
  });

  // Caller must action request. To better encapsulate this we would
  // need to inject the history service here.
  return requestToURL(attachExternalOauth({
    provider,
    state,
    bearer_token: currentToken,
    dmm_env: dmmEnv,
    brand: brandId,
  }));
};

const AttachResponse = t.intersection([
  t.interface({
    state: t.string,
  }),
  t.partial({
    result:  t.string,
    error:   t.string,
    message: t.string,
    code:    t.string,
  }),
]);

export const finishAttachExternalOauth = (query: { [index: string]: unknown }): {
  redirectUriReference: string,
  attached: boolean;
} => {
  const result = decodeOrThrow(AttachResponse, query, (errors) => {
    throw new InvalidResultError(
      `Could not decode result from query\n${errors.join('\n')}`);
  });

  const continuation = loadContinuation(Action.ATTACH, result.state);

  const { redirectUriReference, provider } = continuation;

  if (result.result === 'success') {
    return { redirectUriReference, attached: true };
  }

  if (result.error) {
    if (
      // Regular oauth rejection
      (result.error === OauthError.Codes.ACCESS_DENIED &&
       result.code === OauthError.ExtendedCodes.EXTERNAL_REJECTION) ||

      // The omniauth-facebook gem masks errors, resulting in only
      // SERVER_ERRORS.  We just assume that everything was fine, but
      // the user clicked no.
      (provider.id === FACEBOOK.id &&
       result.error === OauthError.Codes.SERVER_ERROR)
    ) {
      return { redirectUriReference, attached: false };
    }

    if (
      result.error === OauthError.Codes.ACCESS_DENIED &&
      result.code === OauthError.ExtendedCodes.UID_TAKEN
    ) {
      throw new UidTakenError(result.message || result.code, provider);
    }

    // The result contains information in oauth codes, but is not
    // strictly an oauth error, as we're not part of an oauth flow.

    // The expected outcomes are already handled. We can just fall
    // through to unexpected failure.
  }

  // Server neither said `result=success` nor provided an error.  But
  // _did_ provide a valid state, so it's not an impersonation.  What
  // does it mean? It's too hard. We give up.
  throw new AttachError(
    `Unexpected attach response from server: error='${result.error}', code='${result.code}`,
    provider, result.error, result.code);
};
